/**
 * Related panels that behave the same on every record: Documents, Comments,
 * History. Mounted by each detail screen as its module lands.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge, Button, Dropzone, EmptyState, Icon, ListRow, RecordHistory, Select, Textarea, formatDateTime, relativeTime, useConfirm, useToast, errorMessage, Avatar, Switch, SkeletonRows, useDelayed } from "@crc/ui";
import { DOCUMENT_KINDS } from "@crc/shared";
import { api } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useHistory, useInvalidate } from "../../lib/queries.ts";

/* ---------------------------- Documents ---------------------------- */
interface DocDto {
  id: string;
  fileName: string;
  kind: string;
  docType: string;
  description: string | null;
  sizeBytes: number;
  contentType: string;
  enteredAt: string;
  downloadUrl: string;
  previewUrl: string;
  createdBy: string | null;
}
export function DocumentsPanel({ ownerType, ownerId, canUpload = true, filterKind }: { ownerType: string; ownerId: string; canUpload?: boolean; /** show and upload only one kind — the equipment or protocols tab of a location */ filterKind?: string }) {
  const key = ["documents", ownerType, ownerId];
  const { data: raw, isLoading, error, refetch } = useQuery({ queryKey: key, queryFn: () => api.get<{ items: DocDto[] }>(`/documents?ownerType=${ownerType}&ownerId=${ownerId}`) });
  const data = raw && filterKind ? { items: raw.items.filter((d) => d.kind === filterKind) } : raw;
  const [kind, setKind] = useState<string>(filterKind ?? "other");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();
  const invalidate = useInvalidate();
  const showSkeleton = useDelayed(isLoading, 200);
  const upload = async (files: File[]) => {
    setBusy(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.set("file", f);
        form.set("ownerType", ownerType);
        form.set("ownerId", ownerId);
        form.set("kind", kind);
        form.set("docType", kind === "certificate" ? "certificate" : kind === "label_front" || kind === "label_back" ? "label" : kind === "spec" ? "spec_sheet" : "other");
        await api.upload("/documents", form);
      }
      toast.success(`${files.length} file${files.length === 1 ? "" : "s"} uploaded`);
      await invalidate(key);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (d: DocDto) => {
    const r = await confirm({ title: `Remove "${d.fileName}"?`, body: "The file is kept and an administrator can recover it; it just stops showing here.", confirmLabel: "Remove", tone: "danger-soft" });
    if (!r.ok) return;
    try {
      await api.del(`/documents/${d.id}`);
      toast.success("Document removed");
      await invalidate(key);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div>
      {canUpload && (
        <div className="card-body">
          <div className="flex wrap mb-3">
            <span className="small muted">Kind</span>
            <Select small value={kind} onChange={(e) => setKind(e.target.value)} style={{ maxWidth: 200 }} aria-label="Document kind">
              {DOCUMENT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k.replace(/_/g, " ")}
                </option>
              ))}
            </Select>
          </div>
          <Dropzone onFiles={upload} multiple disabled={busy} title={busy ? "Uploading…" : "Attach a document"} accept=".pdf,image/*,.csv,.xlsx,.docx" />
        </div>
      )}
      {showSkeleton && !data && <SkeletonRows rows={3} cols={2} />}
      {error && <EmptyState error title="Could not load documents" text={errorMessage(error)} actions={<Button onClick={() => refetch()}>Retry</Button>} />}
      {data && data.items.length === 0 && <EmptyState icon="file" title="No documents yet" text="Certificates, spec sheets, labels and reports attached to this record will appear here." />}
      <div className="list">
        {data?.items.map((d) => (
          <ListRow
            key={d.id}
            icon="file"
            iconTone="primary"
            title={d.fileName}
            detail={`${d.kind.replace(/_/g, " ")} · ${(d.sizeBytes / 1024).toFixed(0)} KB · ${formatDateTime(d.enteredAt)}`}
            right={
              <div className="cell-actions">
                {d.contentType.startsWith("image/") || d.contentType === "application/pdf" ? (
                  <Button size="sm" variant="ghost" icon="eye" onClick={() => window.open(d.previewUrl, "_blank")}>
                    View
                  </Button>
                ) : null}
                <Button size="sm" variant="ghost" icon="download" onClick={() => window.open(d.downloadUrl, "_blank")}>
                  Download
                </Button>
                {canUpload && <Button size="sm" variant="ghost" icon="x" aria-label="Remove" onClick={() => remove(d)} />}
              </div>
            }
          />
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- Comments ---------------------------- */
interface CommentDto {
  id: string;
  parentCommentId: string | null;
  body: string | null;
  deleted: boolean;
  visibility: "internal" | "shared";
  everShared: boolean;
  author: { id: string; name: string; userType: string };
  createdAt: string;
  editedAt: string | null;
  resolvedAt: string | null;
  mentions: Array<{ personId: string; name: string }>;
  mine: boolean;
}
export function CommentsPanel({ entityType, entityId }: { entityType: string; entityId: string }) {
  const key = ["comments", entityType, entityId];
  const { data, isLoading, error, refetch } = useQuery({ queryKey: key, queryFn: () => api.get<{ items: CommentDto[]; canPost: boolean; canStartThread: boolean; sharedAllowed: boolean }>(`/comments?entityType=${entityType}&entityId=${entityId}`) });
  const [body, setBody] = useState("");
  const [shared, setShared] = useState(false);
  const [replyTo, setReplyTo] = useState<CommentDto | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();
  const invalidate = useInvalidate();
  const { isStaff } = useAuth();
  const showSkeleton = useDelayed(isLoading, 200);

  const post = async () => {
    setBusy(true);
    try {
      await api.post("/comments", { entityType, entityId, body, visibility: shared ? "shared" : "internal", parentCommentId: replyTo?.id ?? null });
      setBody("");
      setReplyTo(null);
      toast.success(shared ? "Shared comment posted — the company can see it" : "Internal comment posted");
      await invalidate(key);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const setVisibility = async (c: CommentDto, visibility: "internal" | "shared") => {
    if (visibility === "internal" && c.everShared) {
      const r = await confirm({ title: "Make internal again?", body: "This comment was already shared; making it internal does not un-send it. The audit trail records that the company saw it.", confirmLabel: "Make internal" });
      if (!r.ok) return;
    }
    try {
      await api.post(`/comments/${c.id}/visibility`, { visibility });
      await invalidate(key);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const remove = async (c: CommentDto) => {
    const r = await confirm({ title: "Delete this comment?", body: "A tombstone stays in the thread and the text remains in the audit trail.", confirmLabel: "Delete", tone: "danger-soft" });
    if (!r.ok) return;
    await api.del(`/comments/${c.id}`).catch((e) => toast.error(errorMessage(e)));
    await invalidate(key);
  };
  const roots = (data?.items ?? []).filter((c) => !c.parentCommentId);
  const replies = (id: string) => (data?.items ?? []).filter((c) => c.parentCommentId === id);

  const render = (c: CommentDto, isReply: boolean) => (
    <div key={c.id} className={`comment ${c.visibility}${isReply ? " reply" : ""}`}>
      <Avatar name={c.author.name} size={30} />
      <div className="c-body">
        <div className="c-meta">
          <b>{c.author.name}</b>
          <span>{relativeTime(c.createdAt)}</span>
          {c.editedAt && <span>· edited</span>}
          <Badge tone={c.visibility === "shared" ? "info" : "neutral"}>{c.visibility === "shared" ? "shared with company" : "internal"}</Badge>
          {c.resolvedAt && <Badge tone="success">resolved</Badge>}
          {c.mentions.length > 0 && <span>· mentions {c.mentions.map((m) => m.name).join(", ")}</span>}
        </div>
        <div className="c-text">{c.deleted ? <i className="muted">Comment deleted</i> : c.body}</div>
        {!c.deleted && (
          <div className="flex wrap mt-2">
            {data?.canPost && !isReply && (
              <Button size="xs" variant="ghost" onClick={() => setReplyTo(c)}>
                Reply
              </Button>
            )}
            {isStaff && data?.sharedAllowed && c.visibility === "internal" && (
              <Button size="xs" variant="ghost" onClick={() => setVisibility(c, "shared")}>
                Share with company
              </Button>
            )}
            {isStaff && c.visibility === "shared" && (
              <Button size="xs" variant="ghost" onClick={() => setVisibility(c, "internal")}>
                Make internal
              </Button>
            )}
            {isStaff && !isReply && (
              <Button size="xs" variant="ghost" onClick={() => api.post(`/comments/${c.id}/resolve`).then(() => invalidate(key))}>
                {c.resolvedAt ? "Reopen" : "Resolve"}
              </Button>
            )}
            {(c.mine || isStaff) && (
              <Button size="xs" variant="ghost" onClick={() => remove(c)}>
                Delete
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {showSkeleton && !data && <SkeletonRows rows={3} cols={2} />}
      {error && <EmptyState error title="Could not load comments" text={errorMessage(error)} actions={<Button onClick={() => refetch()}>Retry</Button>} />}
      {data && roots.length === 0 && <EmptyState icon="comment" title="No comments yet" text={data.canStartThread ? "Comments are internal by default. Sharing one with the company is a deliberate act." : "CRC has not shared anything on this record yet."} />}
      {roots.map((c) => (
        <div key={c.id}>
          {render(c, false)}
          {replies(c.id).map((r) => render(r, true))}
        </div>
      ))}
      {data?.canPost && (
        <div className={`comment-compose${shared ? " shared-mode" : ""}`}>
          {replyTo && (
            <div className="small muted mb-2">
              Replying to <b>{replyTo.author.name}</b>{" "}
              <button type="button" className="link-cell" onClick={() => setReplyTo(null)}>
                cancel
              </button>
            </div>
          )}
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={shared ? "Visible to the company…" : "Internal note — CRC staff only…"} rows={3} aria-label="Comment" />
          <div className="between wrap mt-3">
            <span className={`vis-note ${shared ? "" : "muted"}`} style={shared ? { color: "var(--info)" } : undefined}>
              <Icon name={shared ? "eye" : "lock"} size={14} /> {shared ? "Shared: the company will see this" : "Internal: CRC staff only"}
            </span>
            <div className="flex">
              {data.sharedAllowed && isStaff && !replyTo && <Switch checked={shared} onChange={setShared} label="Share with company" />}
              {!data.sharedAllowed && isStaff && <span className="small muted">Comments here are never shared.</span>}
              <Button variant="primary" size="sm" icon="send" disabled={!body.trim()} loading={busy} onClick={post}>
                {replyTo ? "Reply" : "Post"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- History ----------------------------- */
export function HistoryPanel({ entityType, entityId }: { entityType: string; entityId: string }) {
  const q = useHistory(entityType, entityId);
  const items = (q.data?.pages ?? []).flatMap((p) => p.items).map((a) => ({ id: a.id, at: a.at, prose: a.prose, actorName: a.actorName, reason: a.reason }));
  return <RecordHistory items={items} loading={q.isLoading || q.isFetchingNextPage} error={q.error} onRetry={() => q.refetch()} hasMore={!!q.hasNextPage} onMore={() => q.fetchNextPage()} />;
}
